import { useState, useMemo, useEffect, useCallback } from "react";
import {
	Box,
	Typography,
	Stack,
	Button,
	IconButton,
	Chip,
	Tooltip,
	Card,
	InputAdornment,
	Snackbar,
	Alert,
	GlobalStyles,
	ToggleButton,
	ToggleButtonGroup,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import dayjs from "dayjs";
import { StyledTextField, NormalButton } from "../../components/ui/CommonReusables";
import {
	viewIconSx,
	editIconSx,
	deleteIconSx,
	countChipSx,
	footerPagerSx,
	primaryButtonSx,
	badgeChipSx,
	overflowTagSx,
	managePillSx,
	toggleGroupSx,
} from "./components/buttonStyles";
import { deriveAgentAttendance, flattenTrainings } from "../../utils/curriculum";
import CurriculumAPI from "../../api/CurriculumAPI";
import ConfirmationModal from "../../components/confirmation-modal";
import { stripHtml } from "./utils/html";
import CurriculumFormDialog from "./components/CurriculumFormDialog";
import ViewCurriculumDialog from "./components/ViewCurriculumDialog";
import DeleteCurriculumDialog from "./components/DeleteCurriculumDialog";
import TrainingAttendanceDialog from "./components/TrainingAttendanceDialog";
import ManageAgentBadgesDialog from "./components/ManageAgentBadgesDialog";
import AttendanceCalendar from "./components/AttendanceCalendar";
import TrainingDetailsDialog from "./components/TrainingDetailsDialog";

const MOUNT_ID = "ahg-curriculum-management-admin";

// Elementor constrains the shortcode's ancestor containers to a boxed max-width, which squeezes
// the page into half the viewport. :has() scopes the release to only the chain wrapping our mount
// node, so the sidebar and every other widget on the page keep their own widths.
const ELEMENTOR_WRAPPERS = [
	".elementor-widget-shortcode",
	".elementor-widget-container",
	".elementor-container",
	".e-con-inner",
	".e-con",
	".entry-content",
	".site-main",
];

const fullWidthOverrides = {
	[`#${MOUNT_ID}`]: { width: "100%", maxWidth: "100%" },
	[ELEMENTOR_WRAPPERS.map((selector) => `${selector}:has(#${MOUNT_ID})`).join(", ")]: {
		width: "100% !important",
		maxWidth: "100% !important",
		flexBasis: "100% !important",
	},
};

const EmptyState = ({ label }) => (
	<Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
		<Typography variant="body2">{label}</Typography>
	</Box>
);

const EmptyTableState = () => <EmptyState label="No curriculum found" />;

// Search strip sitting directly above a table, sharing its top border.
const TableToolbar = ({ value, onChange, placeholder }) => (
	<Box
		sx={{
			border: "1px solid #D6E6F5",
			borderBottom: "none",
			borderRadius: "8px 8px 0 0",
			px: 2,
			py: 1.5,
			display: "flex",
			justifyContent: "flex-end",
		}}
	>
		<StyledTextField
			size="small"
			placeholder={placeholder}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			sx={{ width: { xs: "100%", sm: 280 } }}
			slotProps={{
				input: {
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
						</InputAdornment>
					),
				},
			}}
		/>
	</Box>
);

// Shared by both tables so they read as one component.
const gridSx = {
	// v8 drives the header/pinned background from this custom property.
	"--DataGrid-containerBackground": "#1D54A2",
	width: "100%",
	borderColor: "#D6E6F5",
	borderRadius: "0 0 8px 8px",
	backgroundColor: "background.paper",

	// Navy header with white labels, matching the agent-side curriculum tables.
	// Elementor was tinting the label text dark, leaving it unreadable on the navy.
	"& .MuiDataGrid-columnHeader": {
		backgroundColor: "#1D54A2 !important",
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		fontWeight: 700,
		color: "#FFFFFF !important",
		whiteSpace: "normal",
		lineHeight: 1.2,
	},
	"& .MuiDataGrid-columnSeparator": {
		color: "rgba(255, 255, 255, 0.35)",
	},
	"& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
		color: "#FFFFFF !important",
	},
	"& .MuiDataGrid-cell": {
		borderColor: "#EDF4FB",
		color: "#1A3F7A",
	},
	"& .MuiDataGrid-row:hover": {
		backgroundColor: "#F7FBFF",
	},
	"& .MuiDataGrid-footerContainer": {
		borderColor: "#D6E6F5",
	},

	...footerPagerSx,
};

// Beyond this many, the row shows a "+N more" marker and the rest live in the manage dialog.
const MAX_VISIBLE_BADGES = 3;

const getAgentColumns = (onManage) => [
	{
		field: "name",
		headerName: "Agent",
		flex: 1,
		minWidth: 200,
		renderCell: (params) => (
			<Stack justifyContent="center" sx={{ height: "100%" }}>
				<Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }} noWrap title={params.row.name}>
					{params.row.name}
				</Typography>
			</Stack>
		),
	},
	{
		field: "badges",
		headerName: "Training Badges",
		flex: 3,
		minWidth: 320,
		sortable: false,
		renderCell: (params) => {
			const items = params.row.badges;
			const visible = items.slice(0, MAX_VISIBLE_BADGES);
			const overflow = items.length - visible.length;

			return (
				<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, height: "100%", overflow: "hidden" }}>
					{visible.length === 0 ? (
						<Typography sx={{ color: "text.secondary", fontSize: "0.75rem" }}>No badges yet</Typography>
					) : (
						visible.map((badge) => (
							<Chip
								key={badge.trainingId}
								icon={<WorkspacePremiumIcon />}
								label={badge.trainingTitle}
								size="small"
								title={`${badge.trainingTitle} — ${badge.curriculumTitle}`}
								sx={badgeChipSx}
							/>
						))
					)}

					{overflow > 0 && <Chip label={`+${overflow} more`} size="small" sx={overflowTagSx} />}
				</Box>
			);
		},
	},
	{
		field: "actions",
		headerName: "Manage",
		width: 130,
		sortable: false,
		filterable: false,
		headerAlign: "center",
		align: "center",
		renderCell: (params) => (
			<Stack justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
				<Button size="small" startIcon={<SettingsOutlinedIcon />} onClick={() => onManage(params.row)} sx={managePillSx}>
					Manage
				</Button>
			</Stack>
		),
	},
];

const CurriculumManagementAdmin = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const [curricula, setCurricula] = useState([]);
	// The pool the attendance pickers draw from — activated agents only. Deactivated ones who
	// already hold badges still appear in the attendance table; they just cannot be given new ones.
	const [roster, setRoster] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	// The staged attendance change awaiting confirmation, or null when nothing is pending.
	const [attendanceConfirm, setAttendanceConfirm] = useState(null);
	const [attendanceSaving, setAttendanceSaving] = useState(false);
	const [search, setSearch] = useState("");
	const [agentSearch, setAgentSearch] = useState("");
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

	// `curriculum: null` is the create case; the dialog is remounted per target so its rich
	// editors seed from the right content.
	const [form, setForm] = useState({ open: false, curriculum: null });
	const [viewRow, setViewRow] = useState(null);
	const [deleteRow, setDeleteRow] = useState(null);
	const [agentView, setAgentView] = useState("table");
	// Held by id rather than as objects so the dialogs re-read fresh state after each change.
	const [attendanceId, setAttendanceId] = useState(null);
	const [manageAgentId, setManageAgentId] = useState(null);
	const [calendarTrainingId, setCalendarTrainingId] = useState(null);

	const notifyError = useCallback((error, fallback) => {
		setSnackbar({ open: true, message: error?.message || fallback, severity: "error" });
	}, []);

	// Writes re-read the list rather than patching state locally, so the grids always reflect what
	// the server actually stored — including the ids it assigned and any cascade it performed.
	const refresh = useCallback(async () => {
		const data = await CurriculumAPI.getAllCurricula();
		setCurricula(Array.isArray(data) ? data : []);
	}, []);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			try {
				const [curriculaData, agents] = await Promise.all([CurriculumAPI.getAllCurricula(), CurriculumAPI.getAgentRoster()]);

				if (cancelled) return;

				setCurricula(Array.isArray(curriculaData) ? curriculaData : []);
				setRoster(Array.isArray(agents) ? agents : []);
			} catch (error) {
				if (!cancelled) notifyError(error, "Failed to load curricula.");
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [notifyError]);

	const rows = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return curricula;

		return curricula.filter(
			(row) =>
				row.title.toLowerCase().includes(query) ||
				stripHtml(row.description).toLowerCase().includes(query) ||
				row.trainings.some((training) => training.title.toLowerCase().includes(query))
		);
	}, [curricula, search]);

	// Derived from the same state, so both tables stay in sync on every change.
	const agentRows = useMemo(() => deriveAgentAttendance(curricula, roster), [curricula, roster]);
	const agentColumns = useMemo(() => getAgentColumns((row) => setManageAgentId(row.id)), []);
	const allTrainings = useMemo(() => flattenTrainings(curricula), [curricula]);

	// Matches on the agent's name or any training badge they hold.
	const filteredAgentRows = useMemo(() => {
		const query = agentSearch.trim().toLowerCase();
		if (!query) return agentRows;

		return agentRows.filter(
			(row) =>
				row.name.toLowerCase().includes(query) ||
				row.badges.some((badge) => badge.trainingTitle.toLowerCase().includes(query) || badge.curriculumTitle.toLowerCase().includes(query))
		);
	}, [agentRows, agentSearch]);

	// Distinguishes "no matches" from "nothing here yet"; memoized so the overlay isn't remounted each render.
	const agentEmptyState = useMemo(
		() => () => <EmptyState label={agentSearch.trim() ? "No agents match your search" : "No agents yet"} />,
		[agentSearch]
	);

	const attendanceCurriculum = curricula.find((row) => row.id === attendanceId) ?? null;
	const manageAgent = agentRows.find((row) => row.id === manageAgentId) ?? null;
	const calendarTraining = allTrainings.find((training) => training.id === calendarTrainingId) ?? null;

	// Attendance is grantable from three places — the per-training accordion, the calendar's
	// training dialog, and the per-agent badge dialog — but all of them call these two handlers,
	// so staging the confirmation here covers every entry point without touching the dialogs.
	//
	// The dialogs address attendance by curriculum and training, but a training id is globally
	// unique, so the server needs only the second. The first stays in the signature because that
	// is what every call site passes.
	// Takes a single agent or a list — the training accordion marks a whole selection at once,
	// while the per-agent badge dialog works the other way round and only ever sends one.
	const requestMarkAttended = (curriculumId, trainingId, agents) => {
		const selection = [].concat(agents).filter(Boolean);

		if (selection.length === 0) return;

		setAttendanceConfirm({
			kind: "mark",
			trainingId,
			agentIds: selection.map((agent) => agent.id),
			agentNames: selection.map((agent) => agent.name),
			trainingTitle: allTrainings.find((training) => training.id === trainingId)?.title ?? "this training",
		});
	};

	const requestRemoveAttendance = (curriculumId, trainingId, agentId) => {
		const training = allTrainings.find((row) => row.id === trainingId);

		setAttendanceConfirm({
			kind: "remove",
			trainingId,
			agentIds: [agentId],
			// The remove call sites only carry an id, so the name is read back off the roster of
			// the training being edited.
			agentNames: [training?.attendees?.find((attendee) => attendee.id === agentId)?.name ?? "This agent"],
			trainingTitle: training?.title ?? "this training",
		});
	};

	const isRemovingAttendance = "remove" === attendanceConfirm?.kind;

	// Up to three names are spelled out; past that the prompt just counts them, so a large
	// selection cannot push the confirm button off screen.
	const confirmAgentSummary = useMemo(() => {
		const names = attendanceConfirm?.agentNames ?? [];

		if (names.length === 0) return "";
		if (names.length === 1) return names[0];
		if (names.length <= 3) return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

		return `${names.length} agents`;
	}, [attendanceConfirm]);

	const confirmAttendanceChange = async () => {
		if (!attendanceConfirm) return;

		const { kind, trainingId, agentIds, agentNames } = attendanceConfirm;
		const marking = "mark" === kind;

		setAttendanceSaving(true);

		try {
			await (marking ? CurriculumAPI.markAttendance(trainingId, agentIds) : CurriculumAPI.removeAttendance(trainingId, agentIds[0]));
			await refresh();

			setSnackbar({
				open: true,
				message: marking
					? `${agentIds.length === 1 ? agentNames[0] : `${agentIds.length} agents`} marked as attended.`
					: `${agentNames[0]}'s attendance removed.`,
				severity: "success",
			});
		} catch (error) {
			notifyError(error, marking ? "Could not mark attendance." : "Could not remove attendance.");
		} finally {
			// Dismissed either way — nothing changed on a failure, and the snackbar carries the
			// reason, so leaving the prompt up would just strand the admin on it.
			setAttendanceSaving(false);
			setAttendanceConfirm(null);
		}
	};

	/**
	 * The dialog submits the whole trainings list; the server reconciles it against what is
	 * stored, so drafts are forwarded as-is. Rows keep the id they were loaded with — that is what
	 * marks them as edits rather than inserts — and rows dropped from the list are deleted server
	 * side along with the badges earned from them.
	 */
	const handleSave = async (payload) => {
		const editing = form.curriculum;

		const body = {
			title: payload.title,
			description: payload.description,
			trainings: payload.trainings.map((draft) => ({
				id: draft.id ?? 0,
				title: draft.title,
				description: draft.description,
				date: draft.date,
			})),
		};

		setSaving(true);

		try {
			if (editing) {
				const result = await CurriculumAPI.updateCurriculum({ ...body, id: editing.id });
				await refresh();

				const removed = result?.removed_trainings?.length ?? 0;
				setSnackbar({
					open: true,
					message: removed
						? `"${payload.title}" updated. ${removed} training${removed === 1 ? "" : "s"} removed along with their badges.`
						: `"${payload.title}" updated.`,
					severity: "success",
				});
			} else {
				await CurriculumAPI.createCurriculum(body);
				await refresh();
				setSnackbar({ open: true, message: `"${payload.title}" added successfully!`, severity: "success" });
			}

			setForm({ open: false, curriculum: null });
		} catch (error) {
			notifyError(error, "Could not save curriculum.");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		const target = deleteRow;

		setDeleting(true);

		try {
			await CurriculumAPI.deleteCurriculum(target.id);
			await refresh();
			setSnackbar({ open: true, message: `"${target.title}" deleted.`, severity: "success" });
			setDeleteRow(null);
		} catch (error) {
			// The dialog stays open on failure so the delete can be retried from where it was.
			notifyError(error, "Could not delete curriculum.");
		} finally {
			setDeleting(false);
		}
	};

	const columns = [
		{
			field: "title",
			headerName: "Curriculum",
			flex: 1.5,
			minWidth: 200,
			renderCell: (params) => (
				<Stack justifyContent="center" sx={{ height: "100%" }}>
					<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={params.row.title}>
						{params.row.title}
					</Typography>
					<Typography variant="caption" color="text.secondary" noWrap>
						Created {dayjs(params.row.created_at).format("MMM D, YYYY")}
					</Typography>
				</Stack>
			),
		},
		{
			// Widest column — the count/action columns are fixed so the surplus lands here.
			field: "description",
			headerName: "Description",
			flex: 3,
			minWidth: 260,
			sortable: false,
			// Descriptions are rich text; the table shows the plain-text rendering of it.
			valueGetter: (value) => stripHtml(value),
			renderCell: (params) => (
				<Stack justifyContent="center" sx={{ height: "100%" }}>
					<Typography
						variant="body2"
						color="text.secondary"
						title={params.value}
						sx={{
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
							whiteSpace: "normal",
							lineHeight: 1.4,
						}}
					>
						{params.value || "—"}
					</Typography>
				</Stack>
			),
		},
		{
			field: "trainings",
			headerName: "Trainings",
			width: 150,
			headerAlign: "center",
			align: "center",
			valueGetter: (value) => value.length,
			renderCell: (params) => (
				<Tooltip title="Manage training attendance">
					<Chip
						label={`${params.value} ${params.value === 1 ? "Training" : "Trainings"}`}
						size="small"
						onClick={() => setAttendanceId(params.row.id)}
						sx={countChipSx}
					/>
				</Tooltip>
			),
		},
		{
			field: "attendance",
			headerName: "Badges Awarded",
			width: 150,
			headerAlign: "center",
			align: "center",
			valueGetter: (_value, row) => row.trainings.reduce((total, training) => total + training.attendees.length, 0),
		},
		{
			field: "actions",
			headerName: "Action",
			width: 160,
			sortable: false,
			filterable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params) => (
				<Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
					<Tooltip title="View">
						<IconButton size="small" onClick={() => setViewRow(params.row)} aria-label={`View ${params.row.title}`} sx={viewIconSx}>
							<VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
						</IconButton>
					</Tooltip>

					<Tooltip title="Edit">
						<IconButton
							size="small"
							onClick={() => setForm({ open: true, curriculum: params.row })}
							aria-label={`Edit ${params.row.title}`}
							sx={editIconSx}
						>
							<EditOutlinedIcon sx={{ fontSize: 20 }} />
						</IconButton>
					</Tooltip>

					<Tooltip title="Delete">
						<IconButton size="small" onClick={() => setDeleteRow(params.row)} aria-label={`Delete ${params.row.title}`} sx={deleteIconSx}>
							<DeleteOutlineIcon sx={{ fontSize: 20 }} />
						</IconButton>
					</Tooltip>
				</Stack>
			),
		},
	];

	return (
		<>
			<GlobalStyles styles={fullWidthOverrides} />

			<Box sx={{ p: { xs: 2, md: 3 }, width: "100%", minHeight: "100vh" }}>
				{/* Page title — sits above the card */}
				<Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4, mb: 2.5 }}>
					CURRICULUM MANAGEMENT
				</Typography>

				<Card sx={{ p: { xs: 2, md: 3 }, width: "100% !important", backgroundColor: "background.paper" }}>
					{/* Card header — description and primary action, directly above the toolbar */}
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={2}
						justifyContent="space-between"
						alignItems={{ xs: "stretch", sm: "center" }}
						sx={{ mb: 2 }}
					>
						<Box>
							<Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Curriculums</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>
								Create a curriculum, add the trainings under it, and mark which agents attended each one
							</Typography>
						</Box>

						<NormalButton
							startIcon={<AddIcon />}
							onClick={() => setForm({ open: true, curriculum: null })}
							sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" }, ...primaryButtonSx }}
						>
							Add Curriculum
						</NormalButton>
					</Stack>

					{/* Toolbar */}
					<TableToolbar value={search} onChange={setSearch} placeholder="Search curriculum or training..." />

					{/* Table */}
					<DataGrid
						rows={rows}
						columns={columns}
						loading={loading}
						autoHeight
						rowHeight={64}
						disableColumnMenu
						disableRowSelectionOnClick
						showCellVerticalBorder
						showColumnVerticalBorder
						initialState={{ pagination: { paginationModel: { pageSize: 6, page: 0 } } }}
						pageSizeOptions={[6, 12, 24]}
						slots={{ noRowsOverlay: EmptyTableState, noResultsOverlay: EmptyTableState }}
						sx={gridSx}
					/>

					{/* Inverted view — the same attendance data by agent, or plotted on the month it happened */}
					<Box sx={{ mt: 4 }}>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={2}
							justifyContent="space-between"
							alignItems={{ xs: "stretch", sm: "flex-start" }}
							sx={{ mb: 2 }}
						>
							<Box>
								<Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Agent Attendance</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
									{agentView === "table"
										? "The same data by agent — every training badge each agent has earned"
										: "Every training on the day it was held — open one to see its details and who attended"}
								</Typography>
							</Box>

							<ToggleButtonGroup
								exclusive
								size="small"
								value={agentView}
								onChange={(_event, value) => value && setAgentView(value)}
								sx={{ alignSelf: { xs: "flex-start", sm: "center" }, ...toggleGroupSx }}
							>
								<ToggleButton value="table">
									<TableRowsOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
									Table
								</ToggleButton>
								<ToggleButton value="calendar">
									<CalendarMonthOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
									Calendar
								</ToggleButton>
							</ToggleButtonGroup>
						</Stack>

						{agentView === "table" ? (
							<>
								<TableToolbar value={agentSearch} onChange={setAgentSearch} placeholder="Search agent or training..." />

								<DataGrid
									rows={filteredAgentRows}
									columns={agentColumns}
									loading={loading}
									autoHeight
									rowHeight={72}
									disableColumnMenu
									disableRowSelectionOnClick
									showCellVerticalBorder
									showColumnVerticalBorder
									initialState={{ pagination: { paginationModel: { pageSize: 6, page: 0 } } }}
									pageSizeOptions={[6, 12, 24]}
									slots={{ noRowsOverlay: agentEmptyState, noResultsOverlay: agentEmptyState }}
									sx={gridSx}
								/>
							</>
						) : (
							<AttendanceCalendar trainings={allTrainings} onSelectTraining={(training) => setCalendarTrainingId(training.id)} />
						)}
					</Box>
				</Card>
			</Box>

			{/* Mounted only while open so the rich editors always seed from the current curriculum. */}
			{form.open && (
				<CurriculumFormDialog
					key={form.curriculum?.id ?? "new"}
					open
					onClose={() => setForm({ open: false, curriculum: null })}
					onSubmit={handleSave}
					curriculum={form.curriculum}
					submitting={saving}
				/>
			)}

			{viewRow && <ViewCurriculumDialog open onClose={() => setViewRow(null)} curriculum={curricula.find((row) => row.id === viewRow.id)} />}

			<DeleteCurriculumDialog
				open={Boolean(deleteRow)}
				onClose={() => setDeleteRow(null)}
				onConfirm={handleDelete}
				curriculum={deleteRow}
				deleting={deleting}
			/>

			<TrainingAttendanceDialog
				open={Boolean(attendanceCurriculum)}
				onClose={() => setAttendanceId(null)}
				curriculum={attendanceCurriculum}
				roster={roster}
				onMarkAttended={requestMarkAttended}
				onRemoveAttendance={requestRemoveAttendance}
			/>

			<TrainingDetailsDialog
				open={Boolean(calendarTraining)}
				onClose={() => setCalendarTrainingId(null)}
				training={calendarTraining}
				roster={roster}
				onMarkAttended={requestMarkAttended}
				onRemoveAttendance={requestRemoveAttendance}
			/>

			<ManageAgentBadgesDialog
				open={Boolean(manageAgent)}
				onClose={() => setManageAgentId(null)}
				agent={manageAgent}
				allTrainings={allTrainings}
				onMarkAttended={requestMarkAttended}
				onRemoveAttendance={requestRemoveAttendance}
			/>

			{/* Sits above whichever attendance dialog staged the change, and returns to it after. */}
			<ConfirmationModal
				open={Boolean(attendanceConfirm)}
				onClose={() => !attendanceSaving && setAttendanceConfirm(null)}
				onConfirm={confirmAttendanceChange}
				isLoading={attendanceSaving}
				title={isRemovingAttendance ? "Remove attendance?" : "Mark as attended?"}
				description={
					isRemovingAttendance
						? `${confirmAgentSummary} will lose the badge earned from "${attendanceConfirm?.trainingTitle}". You can mark them as attended again later.`
						: `${confirmAgentSummary} will earn the badge for "${attendanceConfirm?.trainingTitle}", dated the day that session was held.`
				}
				confirmText={isRemovingAttendance ? "Yes, Remove" : "Yes, Mark Attended"}
				cancelText="No, Go Back"
				confirmColor={isRemovingAttendance ? "#E14B5A" : "#1D54A2"}
			/>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={3000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				sx={{ zIndex: 9999 }}
			>
				<Alert severity={snackbar.severity} sx={{ width: "100%", fontWeight: 500 }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default CurriculumManagementAdmin;
